import React, { useEffect, useMemo, useState } from 'react';
import { Download, Filter, RefreshCw, Calendar, User as UserIcon, CreditCard } from 'lucide-react';
import vendaService, { VendaResponseDTO } from '../services/vendaService';
import { paymentTypeService } from '../services/paymentTypeService';
import userService, { User } from '../services/userService';

type PaymentTypeOption = { id: string; nome: string };

interface SalesItemRow {
  vendaId: string;
  data: string;
  cliente?: string;
  vendedor?: string;
  status?: string;
  produtoCodigo?: string;
  produtoNome?: string;
  quantidade?: number;
  embalagemSigla?: string;
  valor?: number;
  precoUnitario?: number;
  custoUnitario?: number;
  lucroUnitario?: number;
}

function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function normalizeReportPayload(payload: any): any[] {
  const raw = payload?.data ?? payload;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.content)) return raw.content;
  if (raw && typeof raw === 'object') return [raw];
  return [];
}

function mapToItemRows(obj: any): SalesItemRow[] {
  const v = obj?.venda ?? obj;

  const vendaId = String(
    v?.numeroVenda ?? v?.id ?? v?.codigo ?? v?.numero ?? v?.numeroCupom ?? v?.uuid ?? ''
  );
  const data = v?.dataVenda ?? v?.dataCriacao ?? v?.criadoEm ?? v?.createdAt ?? v?.data ?? '';
  const cliente = v?.clienteNome ?? v?.nomeCliente ?? v?.cliente?.nome ?? v?.cliente ?? undefined;
  const vendedor = v?.vendedorNome ?? v?.vendedor?.nome ?? v?.vendedor ?? undefined;
  const status = v?.status ?? obj?.status ?? undefined;

  const itensArr = Array.isArray(v?.itens)
    ? v.itens
    : (Array.isArray(v?.itensVenda) ? v.itensVenda : (Array.isArray(v?.vendaItens) ? v.vendaItens : []));

  const rows: SalesItemRow[] = [];
  for (const it of itensArr) {
    const produto = it?.produto ?? it?.product ?? {};
    const produtoCodigo = it?.produtoCodigo ?? it?.codigoProduto ?? produto?.codigo ?? produto?.sku ?? undefined;
    const produtoNome = it?.produtoNome ?? it?.nomeProduto ?? produto?.nome ?? produto?.descricao ?? undefined;
    const quantidade = Number(it?.quantidade ?? it?.qtd ?? it?.qtdVendida ?? 0) || undefined;
    const embalagemSigla = it?.embalagemSigla ?? it?.embalagem?.sigla ?? it?.embalagemDescricao ?? undefined;
    const valorUnitario = Number(it?.precoUnitario ?? it?.preco ?? it?.valorUnitario ?? it?.valor ?? 0);
    const custoUnitario = Number(it?.custoUnitario ?? it?.custo ?? it?.valorCusto ?? 0);
    const valorTotalItem = Number(
      it?.valorTotal ?? it?.valorTotalItem ?? it?.total ?? it?.valorVenda ?? (valorUnitario * (quantidade || 1))
    );
    const lucroUnitario = Number.isFinite(custoUnitario)
      ? Number(((valorUnitario - custoUnitario) * (quantidade || 0)).toFixed(2))
      : undefined;

    rows.push({
      vendaId,
      data,
      cliente,
      vendedor,
      status,
      produtoCodigo,
      produtoNome,
      quantidade,
      embalagemSigla,
      valor: valorTotalItem,
      precoUnitario: valorUnitario,
      custoUnitario,
      lucroUnitario,
    });
  }

  return rows;
}

const SalesReportPage: React.FC = () => {
  const [dataInicial, setDataInicial] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return toISODate(d);
  });
  const [dataFinal, setDataFinal] = useState<string>(() => toISODate(new Date()));
  const [tiposPagamentoIds, setTiposPagamentoIds] = useState<string[]>([]);
  const [vendedorId, setVendedorId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [entries, setEntries] = useState<SalesItemRow[]>([]);

  const [paymentTypes, setPaymentTypes] = useState<PaymentTypeOption[]>([]);
  const [sellers, setSellers] = useState<User[]>([]);
  const salesTotal = useMemo(() => entries.reduce((acc, e) => acc + (e.valor || 0), 0), [entries]);

  useEffect(() => {
    // Carregar filtros auxiliares
    (async () => {
      try {
        const pt = await paymentTypeService.list();
        setPaymentTypes(pt.data.map((x) => ({ id: x.id, nome: x.nome })));
      } catch (e) {
        console.warn('Falha ao listar tipos de pagamento', e);
      }
      try {
        const res = await userService.getUsersByRole('VENDEDOR');
        const sellersData = Array.isArray(res.data)
          ? res.data
          : (Array.isArray((res as any)?.data?.content) ? (res as any).data.content : []);
        setSellers(sellersData as User[]);
      } catch (e) {
        console.warn('Falha ao listar vendedores', e);
      }
    })();
  }, []);

  const buscar = async () => {
    setLoading(true);
    try {
      const response = await vendaService.relatorioVendas({ dataInicial, dataFinal, tiposPagamentoIds, vendedorId: vendedorId || undefined, status: status || undefined });
      const list = normalizeReportPayload(response.data);
      const rows = list.flatMap(mapToItemRows);
      // Classificar por id da venda (numérico) ordem decrescente; depois data e código
      const parseVendaNum = (id: string | undefined) => {
        const m = String(id || '').match(/(\d+)/);
        return m ? Number(m[1]) : Number.MIN_SAFE_INTEGER;
      };
      rows.sort((a, b) => {
        const aNum = parseVendaNum(a.vendaId);
        const bNum = parseVendaNum(b.vendaId);
        if (aNum !== bNum) return bNum - aNum; // desc
        const da = new Date(a.data || 0).getTime();
        const db = new Date(b.data || 0).getTime();
        if (da !== db) return da - db;
        return String(a.produtoCodigo || '').localeCompare(String(b.produtoCodigo || ''));
      });
      setEntries(rows);
    } catch (err) {
      console.error('Erro ao carregar relatório de vendas', err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Buscar inicial
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportarCSV = () => {
    const header = ['Data', 'Venda', 'Cliente', 'Vendedor', 'Status', 'Código Produto', 'Nome Produto', 'Quantidade', 'EMB', 'Valor Custo', 'Valor vendido', 'Lucro'];
    const rows = entries.map((e) => [
      e.data,
      e.vendaId,
      e.cliente ?? '',
      e.vendedor ?? '',
      e.status ?? '',
      e.produtoCodigo ?? '',
      e.produtoNome ?? '',
      typeof e.quantidade === 'number' ? String(e.quantidade).replace('.', ',') : '',
      e.embalagemSigla ?? '',
      typeof e.custoUnitario === 'number' ? String(e.custoUnitario).replace('.', ',') : '',
      typeof e.valor === 'number' ? String(e.valor).replace('.', ',') : '',
      typeof e.lucroUnitario === 'number' ? String(e.lucroUnitario).replace('.', ',') : '',
    ]);
    const profitTotal = entries.reduce((acc, e) => acc + (typeof e.lucroUnitario === 'number' ? e.lucroUnitario : 0), 0);
    const summary = [['Total de vendas', '', '', '', '', '', '', '', '', '', String(salesTotal).replace('.', ','), String(profitTotal).replace('.', ',')]];
    const csvLines = [...summary, [], header, ...rows]
      .map((cols) => Array.isArray(cols) ? cols.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';') : '')
      .join('\n');
    const blob = new Blob(["\ufeff" + csvLines], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio-vendas-${dataInicial}_a_${dataFinal}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">Relatório de vendas</h1>
          <p className="text-gray-600 mt-1">Gere o relatório por período e exporte para Excel</p>
        </div>
        <div className="flex gap-3">
          <button onClick={buscar} className="berry-button-secondary flex items-center gap-2" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Carregando...' : 'Atualizar'}
          </button>
          <button onClick={exportarCSV} className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg" disabled={entries.length === 0}>
            <Download className="w-4 h-4" />
            Exportar Excel
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="berry-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Inicial</label>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <input type="date" className="berry-input w-full" value={dataInicial} onChange={(e) => setDataInicial(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Final</label>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <input type="date" className="berry-input w-full" value={dataFinal} onChange={(e) => setDataFinal(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Vendedor (opcional)</label>
            <div className="flex items-center gap-2">
              <UserIcon className="w-4 h-4 text-gray-500" />
              <select className="berry-input w-full" value={vendedorId} onChange={(e) => setVendedorId(e.target.value)}>
                <option value="">Todos</option>
                {sellers.map((s) => (
                  <option key={s.id} value={s.id}>{s.nome || s.username}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Formas de pagamento</label>
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-gray-500" />
              <select multiple className="berry-input w-full h-28" value={tiposPagamentoIds} onChange={(e) => {
                const options = Array.from(e.target.selectedOptions).map((o) => o.value);
                setTiposPagamentoIds(options);
              }}>
                {paymentTypes.map((pt) => (
                  <option key={pt.id} value={pt.id}>{pt.nome}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status da venda</label>
            <div className="flex items-center gap-2">
              <select className="berry-input w-full" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">--</option>
                <option value="PENDENTE">PENDENTE</option>
                <option value="FINALIZADA">FINALIZADA</option>
                <option value="CANCELADA">CANCELADA</option>
                <option value="EM_PROCESSAMENTO">EM_PROCESSAMENTO</option>
                <option value="ENTREGUE">ENTREGUE</option>
                <option value="PARCIALMENTE_ENTREGUE">PARCIALMENTE_ENTREGUE</option>
              </select>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button onClick={buscar} className="berry-button flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Aplicar filtros
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="berry-card p-4">
          <div className="text-sm text-gray-500">Valor total das vendas</div>
          <div className="text-2xl font-semibold">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(salesTotal)}</div>
        </div>
        <div className="berry-card p-4">
          <div className="text-sm text-gray-500">Lucro total</div>
          <div className="text-2xl font-semibold">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(entries.reduce((acc, e) => acc + (e.lucroUnitario || 0), 0))}</div>
        </div>
      </div>

      {/* Tabela por itens da venda */}
      <div className="berry-card p-0 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Venda</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendedor</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código Produto</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome Produto</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantidade</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">EMB</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Valor Custo</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Valor vendido</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Lucro</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {entries.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-center text-gray-500" colSpan={12}>Nenhum resultado para os filtros informados</td>
              </tr>
            )}
            {entries.map((e, idx) => (
              <tr key={`${e.vendaId}-${e.produtoCodigo}-${idx}`} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm text-gray-700">{e.data ? new Date(e.data).toLocaleString('pt-BR') : '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{e.vendaId || '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{e.cliente ?? '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{e.vendedor ?? '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{e.status ?? '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{e.produtoCodigo ?? '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{e.produtoNome ?? '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{typeof e.quantidade === 'number' ? e.quantidade : '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-700">{e.embalagemSigla ?? '-'}</td>
                <td className="px-4 py-2 text-sm text-right text-gray-700">{(typeof e.custoUnitario === 'number' && Number.isFinite(e.custoUnitario)) ? `R$ ${Number(e.custoUnitario).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}</td>
                <td className="px-4 py-2 text-sm text-right font-medium">{typeof e.valor === 'number' ? `R$ ${Number(e.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}</td>
                <td className="px-4 py-2 text-sm text-right text-gray-700">{(typeof e.lucroUnitario === 'number' && Number.isFinite(e.lucroUnitario)) ? `R$ ${Number(e.lucroUnitario).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalesReportPage;